import { Temporal } from '@js-temporal/polyfill'

import type { WeeklyPostingStreakData } from './hooks'

const AT_RISK_POPUP_HOUR = 12
const WEDNESDAY = 3
const MIN_ACCOUNT_AGE_DAYS = 15
const MILLISECONDS_IN_DAY = 24 * 60 * 60 * 1000

export const AT_RISK_STREAK_POPUP_DISMISSED_OBJECT_PREFIX =
  'streak-at-risk-popup'

type Streak = WeeklyPostingStreakData['weeklyPostingStreak']

type ShouldShowAtRiskStreakPopupArgs = {
  streak: Streak | undefined
  accountCreatedAt?: string | null
  organizationId?: string | null
  timezone?: string | null
  dismissedObjectIds?: string[]
  currentDate?: Date
}

export const getAtRiskStreakPopupDismissedObjectId = ({
  organizationId,
  timezone,
  currentDate = new Date(),
}: {
  organizationId: string
  timezone: string
  currentDate?: Date
}): string | null => {
  try {
    const now = getZonedDateTime({ currentDate, timezone })
    const wednesdayNoon = getWednesdayNoon(now)
    const date = wednesdayNoon.toPlainDate().toString()

    return `${AT_RISK_STREAK_POPUP_DISMISSED_OBJECT_PREFIX}-${organizationId}-${date}`
  } catch {
    return null
  }
}

export const shouldShowAtRiskStreakPopup = ({
  streak,
  accountCreatedAt,
  organizationId,
  timezone,
  dismissedObjectIds = [],
  currentDate = new Date(),
}: ShouldShowAtRiskStreakPopupArgs): boolean => {
  if (
    !streak ||
    streak.userStreakState !== 'ExistingStreakAtRisk' ||
    streak.count <= 1 ||
    !accountCreatedAt ||
    !organizationId ||
    !timezone
  ) {
    return false
  }

  if (!isAccountOlderThanMinimumAge({ accountCreatedAt, currentDate })) {
    return false
  }

  const dismissedObjectId = getAtRiskStreakPopupDismissedObjectId({
    organizationId,
    timezone,
    currentDate,
  })
  if (!dismissedObjectId) {
    return false
  }

  if (dismissedObjectIds.includes(dismissedObjectId)) {
    return false
  }

  try {
    const now = getZonedDateTime({ currentDate, timezone })
    const wednesdayNoon = getWednesdayNoon(now)

    return Temporal.ZonedDateTime.compare(now, wednesdayNoon) >= 0
  } catch {
    return false
  }
}

function getZonedDateTime({
  currentDate,
  timezone,
}: {
  currentDate: Date
  timezone: string
}): Temporal.ZonedDateTime {
  return Temporal.Instant.fromEpochMilliseconds(
    currentDate.getTime(),
  ).toZonedDateTimeISO(timezone)
}

function getWednesdayNoon(
  date: Temporal.ZonedDateTime,
): Temporal.ZonedDateTime {
  const daysFromWednesday = date.dayOfWeek - WEDNESDAY
  const startOfDay = date.startOfDay()
  const wednesdayStart =
    daysFromWednesday >= 0
      ? startOfDay.subtract({ days: daysFromWednesday })
      : startOfDay.add({ days: Math.abs(daysFromWednesday) })

  return wednesdayStart.add({ hours: AT_RISK_POPUP_HOUR })
}

function isAccountOlderThanMinimumAge({
  accountCreatedAt,
  currentDate,
}: {
  accountCreatedAt: string
  currentDate: Date
}): boolean {
  const createdAtDate = new Date(accountCreatedAt)
  if (Number.isNaN(createdAtDate.getTime())) {
    return false
  }

  return (
    currentDate.getTime() - createdAtDate.getTime() >
    MIN_ACCOUNT_AGE_DAYS * MILLISECONDS_IN_DAY
  )
}
